import { Add, Delete, Link as LinkIcon } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { Box, Button, Grid, IconButton, Paper, Tooltip } from "@mui/material";
import CustomTextField from "../../../../components/common/Textfield";
import SectionHeader from "./SectionHeader";
import SocialIcon from "./SociaIcon";

const uid = () => Math.random().toString(36).slice(2, 9);

const SOCIAL_OPTIONS = [
  { value: "Facebook", icon: "Facebook" },
  { value: "Instagram", icon: "Instagram" },
  { value: "LinkedIn", icon: "LinkedIn" },
  { value: "YouTube", icon: "YouTube" },
  { value: "Twitter", icon: "Twitter" },
  { value: "Other", icon: "Language" },
];

export interface SocialLink {
  id: string;
  platform: string;
  url: string;
  icon: string;
}

interface Props {
  socials: SocialLink[];
  setSocials: React.Dispatch<React.SetStateAction<SocialLink[]>>;
  primaryColor: string;
  secondaryColor: string;
  bgColor: string;
}

const SocialsPanel: React.FC<Props> = ({
  socials,
  setSocials,
  primaryColor,
  secondaryColor,
  bgColor,
}) => {
  const addSocial = () =>
    setSocials((prev) => [
      ...prev,
      { id: uid(), platform: "Other", url: "", icon: "Language" },
    ]);

  const updateSocial = (id: string, field: keyof SocialLink, value: string) =>
    setSocials((prev) =>
      prev.map((s) =>
        s.id === id
          ? {
              ...s,
              [field]: value,
              icon: field === "platform" ? value : s.icon,
            }
          : s,
      ),
    );

  const removeSocial = (id: string) =>
    setSocials((prev) => prev.filter((s) => s.id !== id));

  return (
    <Box>
      <SectionHeader title="Social Media Links" color={primaryColor} />
      <AnimatePresence>
        {socials.map((social) => (
          <motion.div
            key={social.id}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <Paper
              sx={{
                p: 2,
                mb: 2,
                boxShadow: 0,
                backgroundColor: bgColor,
                borderRadius: 2,
                border: `1px solid ${primaryColor}22`,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SocialIcon
                      name={social.platform}
                      sx={{ color: secondaryColor }}
                    />
                    <CustomTextField
                      select
                      fullWidth
                      size="small"
                      value={social.platform}
                      onChange={(e) =>
                        updateSocial(social.id, "platform", e.target.value)
                      }
                      SelectProps={{ native: true }}
                    >
                      {SOCIAL_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.value}
                        </option>
                      ))}
                    </CustomTextField>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <CustomTextField
                    fullWidth
                    size="small"
                    placeholder="https://..."
                    value={social.url}
                    onChange={(e) =>
                      updateSocial(social.id, "url", e.target.value)
                    }
                    InputProps={{
                      startAdornment: (
                        <LinkIcon
                          sx={{ color: secondaryColor, mr: 1, fontSize: 16 }}
                        />
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 1 }}>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() => removeSocial(social.id)}
                      sx={{ color: "error.main" }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>
        ))}
      </AnimatePresence>
      <Button
        startIcon={<Add />}
        variant="outlined"
        size="small"
        onClick={addSocial}
        sx={{
          borderColor: primaryColor,
          color: primaryColor,
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 600,
          "&:hover": { backgroundColor: `${primaryColor}12` },
        }}
      >
        Add Social Link
      </Button>
    </Box>
  );
};

export default SocialsPanel;
